import React, { useState, useMemo } from 'react';
import Header from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import KPICard from '../components/ui/KPICard';
import DateRangePicker from '../components/ui/DateRangePicker';
import { useEstimates } from '../hooks/useEstimates';
import { formatCurrency, formatDate, toLocalDateStr } from '../utils/formatters';

const toDateStr = toLocalDateStr;
function getYtdRange() {
  const now = new Date();
  return { start: toDateStr(new Date(now.getFullYear(), 0, 1)), end: toDateStr(now) };
}

export default function EstimatesPage() {
  const [statusFilter, setStatusFilter] = useState(''); // '', 'sold', 'unsold'
  const [dateRange, setDateRange] = useState(getYtdRange);
  const { data, loading, error } = useEstimates(statusFilter, dateRange.start, dateRange.end);
  // KPI cards always need the full unfiltered set (regardless of which tab is active) so the
  // customer-level "any estimate sold" exclusion below has all of a customer's estimates to
  // check against, not just the ones in the currently filtered/date-ranged view.
  const { data: allData } = useEstimates('', dateRange.start, dateRange.end);

  const stats = useMemo(() => {
    // A customer drops out of "unsold" entirely once ANY of their estimates gets sold -
    // they already said yes to something, so their other still-Open estimates (even on a
    // different job) are leftover admin, not real opportunities. Matches the same rule
    // applied server-side in routes/estimates.js.
    const soldCustomerIds = new Set(
      allData.filter(e => e.status === 'Sold' && e.customer_id != null).map(e => e.customer_id)
    );
    const isOpenOpportunity = (e) => e.status !== 'Sold' && !soldCustomerIds.has(e.customer_id);

    const soldData = allData.filter(e => e.status === 'Sold');
    const unsoldData = allData.filter(isOpenOpportunity);
    const totalCount = new Set(allData.map(e => e.customer_id)).size;
    const soldCount = new Set(soldData.map(e => e.customer_id)).size;
    const unsoldCount = new Set(unsoldData.map(e => e.customer_id)).size;
    const soldValue = soldData.reduce((s, e) => s + (e.total || 0), 0);
    const potentialValue = unsoldData.filter(e => e.tier === 'Better').reduce((s, e) => s + (e.total || 0), 0);

    return { totalCount, soldCount, unsoldCount, soldValue, potentialValue };
  }, [allData]);

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Best': return { bg: '#dcfce7', text: '#166534', border: '#bcf0da' };
      case 'Better': return { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' };
      case 'Good': return { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' };
      default: return { bg: '#f8fafc', text: '#64748b', border: '#e2e8f0' };
    }
  };

  return (
    <div>
      <Header title="Estimates (Good, Better, Best)" />
      <div style={st.page}>
        <ErrorBanner message={error} />

        <DateRangePicker startDate={dateRange.start} endDate={dateRange.end} onChange={setDateRange} defaultPresetIdx={4} />

        {/* Toolbar */}
        <div style={st.toolbar}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setStatusFilter('')} style={{ ...st.filterBtn, ...(statusFilter === '' ? st.filterBtnActive : {}) }}>All Estimates</button>
            <button onClick={() => setStatusFilter('sold')} style={{ ...st.filterBtn, ...(statusFilter === 'sold' ? st.filterBtnActive : {}) }}>Sold</button>
            <button onClick={() => setStatusFilter('unsold')} style={{ ...st.filterBtn, ...(statusFilter === 'unsold' ? st.filterBtnActive : {}) }}>Unsold</button>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>
            Showing {data.length} Estimates
          </div>
        </div>

        {/* stats */}
        <div style={st.kpiRow}>
          <KPICard title="Total Customers" value={stats.totalCount} icon="📝" color="#3b82f6" loading={loading} sub="Unique customers with an estimate" />
          <KPICard title="Sold Customers" value={stats.soldCount} icon="✅" color="#22c55e" loading={loading} sub={formatCurrency(stats.soldValue)} />
          <KPICard title="Unsold (Potential)" value={stats.unsoldCount} icon="⏳" color="#f59e0b" loading={loading} sub={formatCurrency(stats.potentialValue)} note="Count = customers with an open estimate · Value = total of Better-tier options only" />
          <KPICard title="Sold Rate" value={stats.totalCount > 0 ? `${Math.round((stats.soldCount / stats.totalCount) * 100)}%` : '0%'} icon="📊" color="#8b5cf6" loading={loading} />
        </div>

        {/* Main Card */}
        <div style={st.card}>
          <div style={st.cardHeader}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>Itemized Estimates</h3>
          </div>
          
          {loading ? <LoadingSpinner /> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={st.table}>
                <thead>
                  <tr style={st.thead}>
                    <th style={st.th}>Account</th>
                    <th style={st.th}>Job Completion Date</th>
                    <th style={st.th}>Estimate #</th>
                    <th style={st.th}>Customer</th>
                    <th style={st.th}>Job ID</th>
                    <th style={st.th}>Status</th>
                    <th style={st.th}>GBB Tier</th>
                    <th style={{ ...st.th, textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                        No estimates found for the selected filter.
                      </td>
                    </tr>
                  ) : data.map(e => {
                    const colors = getTierColor(e.tier);
                    return (
                      <tr key={e.id} style={st.tr}>
                        <td style={{ ...st.td, fontWeight: '600', color: '#6366f1' }}>{e.account_name || e.account_id}</td>
                        <td style={st.td}>{formatDate(e.relevant_date || e.created_on)}</td>
                        <td style={{ ...st.td, fontWeight: '600', color: '#0f172a' }}>{e.estimate_number}</td>
                        <td style={st.td}>{e.customer_name || '—'}</td>
                        <td style={st.td}>{e.job_id}</td>
                        <td style={st.td}>
                          <span style={{ 
                            padding: '0.2rem 0.6rem', 
                            borderRadius: '999px', 
                            fontSize: '0.7rem', 
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            background: e.status === 'Sold' ? '#dcfce7' : '#f1f5f9',
                            color: e.status === 'Sold' ? '#166534' : '#475569'
                          }}>
                            {e.status}
                          </span>
                        </td>
                        <td style={st.td}>
                          <span style={{ 
                            padding: '0.25rem 0.75rem', 
                            borderRadius: '0.375rem', 
                            fontSize: '0.75rem', 
                            fontWeight: '600',
                            background: colors.bg,
                            color: colors.text,
                            border: `1px solid ${colors.border}`
                          }}>
                            {e.tier}
                          </span>
                          {e.optionCount > 1 && (
                            <span style={{ marginLeft: '0.5rem', color: '#94a3b8', fontSize: '0.7rem' }}>
                              (Option {e.optionIndex} of {e.optionCount})
                            </span>
                          )}
                        </td>
                        <td style={{ ...st.td, textAlign: 'right', fontWeight: '800', color: '#0f172a' }}>
                          {formatCurrency(e.total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const st = {
  page: { padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: '1rem',
    background: '#fff', padding: '1rem', borderRadius: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  },
  filterBtn: {
    padding: '0.5rem 1.25rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0',
    background: '#fff', color: '#64748b', fontSize: '0.875rem', fontWeight: '600',
    cursor: 'pointer', transition: 'all 0.2s',
  },
  filterBtnActive: {
    background: '#3b82f6', color: '#fff', borderColor: '#3b82f6'
  },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' },
  card: { background: '#fff', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
  cardHeader: { padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: '#f8fafc' },
  th: { padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.025em' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' },
  td: { padding: '1rem 1.5rem', fontSize: '0.875rem', color: '#475569' },
};
